import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import useSEO from "../lib/useSEO";
import { ChevronRight, Home } from "lucide-react";
import FindAttorneyPage from "./FindAttorneyPage";

/**
 * LocationDirectoryPage — handles the three SEO routes:
 *   /attorneys/:stateSlug
 *   /attorneys/:stateSlug/:countySlug
 *   /attorneys/:stateSlug/:countySlug/:categorySlug
 *
 * All three render the same filtered directory; only the H1, breadcrumbs,
 * and <title>/meta change.
 */
export default function LocationDirectoryPage() {
    const { stateSlug, countySlug, categorySlug } = useParams();
    const [filters, setFilters] = useState(null);

    useEffect(() => {
        (async () => {
            try { const r = await api.get("/find-attorneys/filters"); setFilters(r.data); }
            catch { /* ignore */ }
        })();
    }, []);

    const ctx = useMemo(() => {
        if (!filters) return null;
        const stateObj = filters.states.find((s) => s.slug === stateSlug);
        const countyObj = stateObj && countySlug
            ? filters.counties.find((c) => c.state_code === stateObj.code && c.slug === countySlug)
            : null;
        const categoryObj = categorySlug ? filters.categories.find((c) => c.slug === categorySlug) : null;
        return { stateObj, countyObj, categoryObj };
    }, [filters, stateSlug, countySlug, categorySlug]);

    const stateName = ctx?.stateObj?.name;
    const countyName = ctx?.countyObj?.name;
    const categoryName = ctx?.categoryObj?.name;

    const headline = categoryName
        ? `${categoryName} Attorneys in ${countyName || stateName}`
        : countyName
            ? `Attorneys in ${countyName}, ${stateName}`
            : stateName
                ? `${stateName} Attorneys`
                : "Find an attorney";

    useSEO({
        title: `${headline} | LocalLawFinder`,
        description: categoryName
            ? `Find ${categoryName.toLowerCase()} attorneys in ${countyName || stateName} on LocalLawFinder. Verified profiles, client reviews, and direct contact options.`
            : countyName
                ? `Browse attorneys serving ${countyName}, ${stateName}. Filter by practice area, see client ratings, and reach out directly.`
                : `Browse attorneys licensed in ${stateName || "your state"}. Filter by county and practice area, see client ratings, and contact attorneys directly.`,
    });

    if (!filters) return null; // tiny flash; FindAttorneyPage shows its own loader after.
    if (stateSlug && !ctx?.stateObj) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="location-not-found">
                <h1 className="font-serif text-2xl font-semibold mb-2">State not found</h1>
                <p className="text-sm text-muted-foreground">We couldn't find that state. Try browsing the full directory.</p>
                <Link to="/find-an-attorney" className="inline-block mt-6 text-primary hover:underline">Browse all attorneys →</Link>
            </div>
        );
    }
    if (countySlug && !ctx?.countyObj) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="location-not-found">
                <h1 className="font-serif text-2xl font-semibold mb-2">County not found in {stateName}</h1>
                <Link to={`/attorneys/${stateSlug}`} className="inline-block mt-6 text-primary hover:underline">← All attorneys in {stateName}</Link>
            </div>
        );
    }

    const hero = (
        <header className="mb-6" data-testid="location-hero">
            <nav className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap mb-3" data-testid="location-breadcrumbs">
                <Link to="/" className="hover:text-foreground inline-flex items-center gap-1"><Home className="h-3 w-3" /> Home</Link>
                <ChevronRight className="h-3 w-3" />
                <Link to="/find-an-attorney" className="hover:text-foreground">Find an attorney</Link>
                {stateName && (<>
                    <ChevronRight className="h-3 w-3" />
                    <Link to={`/attorneys/${stateSlug}`} className="hover:text-foreground">{stateName}</Link>
                </>)}
                {countyName && (<>
                    <ChevronRight className="h-3 w-3" />
                    <Link to={`/attorneys/${stateSlug}/${countySlug}`} className="hover:text-foreground">{countyName}</Link>
                </>)}
                {categoryName && (<><ChevronRight className="h-3 w-3" /><span>{categoryName}</span></>)}
            </nav>
            <h1 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight" data-testid="location-headline">{headline}</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                Listings combine attorneys who have an active paid placement here with attorneys who self-list this service area. Featured and Pro placements appear first.
            </p>
        </header>
    );

    return (
        <FindAttorneyPage
            initialState={ctx?.stateObj?.slug || ""}
            initialCounty={ctx?.countyObj?.slug || ""}
            initialCategory={ctx?.categoryObj?.slug || ""}
            hero={hero}
        />
    );
}
